import { useCallback, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { HttpsStatus } from "../../../../services/utilty";
import { formatPhoneNumber } from "../../services/call.service";
import { VIDEO_CALL } from "../../../../../components/voice-call/constants/video-call-constants";
export const useAddEditContactModalHook = (props = {}) => {
    const { initialValues = {}, onSubmit } = props;
    const modalRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [snackBarProps, setSnackBarProps] = useState({
        type: 'success',
        children: '',
        isShown: false
    });
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [residents, setResidents] = useState([]);
    const [languages] = useState([
        { label: "English", value: "EN" },
        { label: "Spanish", value: "ES" }
    ]);
    const [relationshipOptions] = useState(
        VIDEO_CALL.getRelationshipTypes().map(type => ({
            label: type.value,
            value: type.backendValue
        }))
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form validation schema
    const validationSchema = Yup.object({
        resident: isEdit ? Yup.mixed() : Yup.object().required("Resident is required"),
        firstName: Yup.string().required("First name is required"),
        lastName: Yup.string().required("Last name is required"),
        relationship: Yup.object().required("Relationship is required"),
        language: Yup.object().required("Preferred language is required"),
        phoneNumber: Yup.string()
            .test('phone-format', 'Phone number must be in format: +1 (###) ###-####', function(value) {
                // Allow empty values (optional field)
                if (!value || value === undefined || value.trim() === '') {
                    return true;
                }
                // If value exists, validate format
                return /^\+1 \(\d{3}\) \d{3}-\d{4}$/.test(value);
            }),
        email: Yup.string()
            .required("Email is required")
            .email("Invalid email format"),
        // File is optional - explicitly handle null/undefined values
        file: Yup.mixed().nullable().notRequired()
    });
    // Initialize formik
    const formik = useFormik({
        initialValues: props.initialValues,
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            setIsLoading(true);
            try {
                const payload = {};

                // Add all form fields except file (handle file separately)
                Object.keys(values).forEach(key => {
                    if (key === 'file') {
                        // Skip file field - handle it separately
                        return;
                    }
                    if (key === 'resident' || key === 'language' || key === 'relationship') {
                        payload[key] = values[key]?.value || '';
                    } else {
                        payload[key] = values[key];
                    }
                });
                // Add image only if a file is actually selected
                if (selectedFile) {
                    payload['image'] = selectedFile;
                }
                // Call the provided onSubmit prop or handle submission internally
                if (onSubmit) {
                    const response = await onSubmit(payload, isEdit);
                    if (response.status === HttpsStatus.OK) {
                        setSnackBarProps({
                            type: 'success',
                            children: response.msg,
                            isShown: true
                        });
                        setTimeout(() => {
                            handleClose();
                        }, 2000);
                    } else {
                        formik.setFieldError("email", response.msg);
                        return;
                    }
                }

                // Reset form after successful submission
                if (!isEdit) {
                    formik.resetForm();
                    clearSelectedFile();
                }
            } catch (err) {
                // Submission error handled via formik/snackBar
            } finally {
                setIsSubmitting(false);
                setIsLoading(false);
            }
        }
    });

    // Effect to load residents data
    useEffect(() => {
        // If initialValues has an id, set isEdit to true
        if (initialValues.id) {
            setIsEdit(true);
            setPreviewUrl(initialValues.imageUrl);
            setFormikInitialValues(initialValues);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFormikInitialValues is stable, initialValues intentionally triggers once
    }, [initialValues]);

    useEffect(() => {
        setIsLoading(props.isLoading);
    }, [props.isLoading]);

    useEffect(() => {
        setResidents(props.residents);
    }, [props.residents]);

    const setFormikInitialValues = (data) => {
        const language = languages.find(lang => lang.value === data.language);
        const relationship = relationshipOptions.find(rel => rel.value === data.relationship);

        // Use setFieldValue for each field instead of setValues for better test compatibility
        formik.setValues({
            ...data,
            phoneNumber: formatPhoneNumber(data.phoneNumber),
            language,
            relationship,
            file: null // Explicitly set file to null for edit mode
        });
        
        // Only set selectedFile if there's actually an image URL
        if (data.imageUrl) {
            // Create a fake file object only if there's an existing image
            const file = new File(['file contents'], `id-${data.id}.png`, {
                type: 'image/png',
            });
            setSelectedFile(file);
        } else {
            // No existing image, keep file fields null
            setSelectedFile(null);
        }
    }
    const validateFile = (file) => {
        // Return null for null/undefined files (optional field)
        if (!file) {
            return null;
        }

        // Check file type
        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            return 'File must be JPG, PNG, or WebP format';
        }

        // Check file size (1MB = 1048576 bytes)
        if (file.size > 1048576) {
            return 'File size must be less than 1MB';
        }

        return null;
    };

    const autoCompleteChangeHandler = (name, value) => {
        formik.setFieldValue(name, value);
    };
    const handleFileSelect = (file) => {
        const validationError = validateFile(file);

        if (validationError) {
            setError(validationError);
            setSelectedFile(null);
            setPreviewUrl(null);
            formik.setFieldError('file', validationError);
            formik.setFieldTouched('file', false);
            return;
        }

        setError(null);
        setSelectedFile(file);
        formik.setFieldValue('file', file, false); // Don't validate immediately
        formik.setFieldTouched('file', false); // Reset touched state

        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result);
            // Validate the form after the preview is loaded
            formik.validateForm().then(() => {
                // This ensures the form validation state is updated
                formik.setFieldTouched('file', false);
            });
        };
        reader.readAsDataURL(file);
    };

    const onDrop = useCallback(acceptedFiles => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            handleFileSelect(acceptedFiles[0]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleFileSelect is stable
    }, []);

    const onDragEnter = useCallback(() => {
        setIsDragging(true);
    }, []);

    const onDragLeave = useCallback(() => {
        setIsDragging(false);
    }, []);

    const onFileInputChange = useCallback((event) => {
        if (event.target.files && event.target.files.length > 0) {
            handleFileSelect(event.target.files[0]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleFileSelect is stable
    }, []);

    const clearSelectedFile = useCallback(() => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setError(null);
        formik.setFieldValue('file', null);
        formik.setFieldTouched('file', false);
    }, [formik]);

    const handleClose = useCallback(() => {
        formik.resetForm();
        clearSelectedFile();
        if (props.onClose) {
            props.onClose();
        }
        if (modalRef.current && modalRef.current.handleClose) {
            modalRef.current.handleClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props.onClose intentionally not in deps
    }, [formik, clearSelectedFile]);

    // Format phone number as user types
    const relationshipChangeHandler = (event, data) => {
        autoCompleteChangeHandler("relationship", data);
    };
    const languageChangeHandler = (event, data) => {
        autoCompleteChangeHandler("language", data);
    };
    const residentChangeHandler = (event, data) => {
        autoCompleteChangeHandler("resident", data);
    };

    const handlePhoneChange = useCallback((e) => {
        const formattedValue = formatPhoneNumber(e.target.value);
        formik.setFieldValue('phoneNumber', formattedValue);
    }, [formik]);

    return {
        modalRef,
        selectedFile,
        previewUrl,
        isDragging,
        error,
        isEdit,
        residents,
        languages,
        relationshipOptions,
        isSubmitting,
        formik,
        isLoading,
        snackBarProps,
        onDrop,
        onDragEnter,
        onDragLeave,
        onFileInputChange,
        clearSelectedFile,
        handleClose,
        handlePhoneChange,
        handleFileSelect, // Export this function for testing,
        autoCompleteChangeHandler,
        relationshipChangeHandler,
        languageChangeHandler,
        residentChangeHandler
    };
};
